// src/components/ProcessingResults/GridStage.jsx
import React, { useEffect, useState } from 'react';
import { Stage, Layer, Circle, Text } from 'react-konva';

const GridStage = ({
  jobData,
  rotatedImageRef,
  gridStructure,
  selectedCells,
  colorMode,
  displayGridColors, // Receive calculated colors from parent
  colorsCalculated, // Receive calculation status
  onCellSelect,
  onStatusUpdate,
  skipProcessing
}) => {
  const [gridCircles, setGridCircles] = useState([]);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });

  // Update grid circles when colors or structure changes
  useEffect(() => {
    if (rotatedImageRef.current && displayGridColors.length > 0 && !skipProcessing) {
      console.log("updateGridCircles");
      updateGridCircles();
    }
  }, [displayGridColors, colorsCalculated, skipProcessing]);

  const getRowLetter = (index) => {
    return String.fromCharCode(65 + index); // 65 is 'A' in ASCII
  };

  const updateGridCircles = () => {
    const image = rotatedImageRef.current;
    if (!image ||  displayGridColors.length === 0) return;

    // Calculate stage size
    const maxWidth = 800;
    const maxHeight = 600;
    const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);

    setStageSize({
      width: image.width * scale,
      height: image.height * scale
    });

    const circles = [];
    const rows = displayGridColors.length;
    const cols = displayGridColors[0]?.length || 0;

    // Calculate circle position based on grid
    const cellSpacingX = (image.width * scale) / cols;
    const cellSpacingY = (image.height * scale) / rows;

    for (let row = 0; row < rows; row++) {
      circles[row] = [];
      for (let col = 0; col < cols; col++) {
//         const cell = gridStructure[row][col];
        const colorData = displayGridColors[row] && displayGridColors[row][col] ? displayGridColors[row][col] : null;

        circles[row].push({
          id: colorData.id,
          row: colorData.row,
          col: colorData.col,
          x: col * cellSpacingX,
          y: row * cellSpacingY,
          color: colorData?.color || 'rgb(255, 255, 255)',
          originalColor: colorData?.originalColor || 'rgb(255, 255, 255)',
          r: colorData?.r || 255,
          g: colorData?.g || 255,
          b: colorData?.b || 255,
          confidence: colorData.confidence,
          isComputed: colorData.isComputed,
        });
      }
    }

    setGridCircles(circles);

    const totalCells = circles.reduce((total, row) => total + row.length, 0);
    onStatusUpdate(`Grid: ${rows}x${cols}, Mode: ${colorMode}, Colors displayed: ${totalCells}`);
  };

  const handleCircleClick = (circleId) => {
    const isSelected = selectedCells.includes(circleId);
    onCellSelect(circleId, !isSelected);
  };

  const handleCircleHover = (circle) => {
    const confidence = circle.confidence ? (circle.confidence * 100).toFixed(1) : 'N/A';
    const computedStatus = circle.isComputed ? 'Computed' : 'Detected';
    onStatusUpdate(`Cell (${circle.row}, ${circle.col}) - ${computedStatus} - Color: ${circle.originalColor}, Confidence: ${confidence}%`);
  };

  if (skipProcessing) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center text-muted">
          <i className="bi bi-hourglass-split fs-1"></i>
          <div className="mt-2">Waiting for rotated image...</div>
        </div>
      </div>
    );
  }

  if (!colorsCalculated || displayGridColors.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center text-muted">
          <i className="bi bi-palette fs-1"></i>
          <div className="mt-2">Calculating colors...</div>
        </div>
      </div>
    );
  }

  const rows = displayGridColors.length;
  const cols = displayGridColors[0]?.length || 0;

  // Calculate positions for labels
  const cellSpacingX = stageSize.width / cols;
  const cellSpacingY = stageSize.height / rows;
  const labelOffset = 20; // Space for labels
  const radius = Math.min(cellSpacingX / 3, cellSpacingY / 3, 20);

  return (
    <div className="grid-stage">
      <Stage width={stageSize.width + labelOffset * 4} height={stageSize.height + labelOffset * 4}>
        <Layer>
          {/* Render column numbers (1, 2, 3, ...) */}
          {Array.from({ length: cols }).map((_, col) => (
            <Text
              key={`col-${col}`}
              x={col * cellSpacingX + labelOffset * 2}
              y={labelOffset / 2}
              text={(col + 1).toString()}
              fontSize={2 * radius}
              fill="#333"
              align="center"
              width={cellSpacingX}
            />
          ))}

          {/* Render row letters (A, B, C, ...) */}
          {Array.from({ length: rows }).map((_, row) => (
            <Text
              key={`row-${row}`}
              x={labelOffset / 2}
              y={row * cellSpacingY + labelOffset * 2}
              text={getRowLetter(row)}
              fontSize={2 * radius}
              fill="#333"
              align="center"
              verticalAlign="middle"
              height={cellSpacingY}
            />
          ))}

          {/* Render circles */}
          {gridCircles.map((row, rowIndex) =>
            row.map((circle) => (
              <Circle
                key={circle.id}
                x={4 * labelOffset + circle.x}
                y={4 * labelOffset + circle.y}
                radius={radius}
                fill={circle.color}
                stroke={selectedCells.includes(circle.id) ? 'blue' : 'black'}
                strokeWidth={selectedCells.includes(circle.id) ? 3 : 1}
                onClick={() => handleCircleClick(circle.id)}
                onMouseEnter={() => handleCircleHover(circle)}
                onMouseLeave={() => onStatusUpdate('')}
                opacity={1.0}
              />
            ))
          )}
        </Layer>
      </Stage>
    </div>
  );
};

export default GridStage;